import { SectionCollapsibleProps } from '@/renderer/types/sales';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  selectCanCreateOrders,
  selectDiningSectionById,
  selectDiningTableOrder,
  selectEffectiveOpenDiningSectionId,
  selectSelectedTableId,
  setOpenDiningSection,
} from '@store/runningOrder';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React from 'react';
import { TableCard } from './TableCard';
import { AnimatedCollapsible } from '../common';

export const SectionCollapsible: React.FC<SectionCollapsibleProps> = ({
  isTourPrimary = false,
  sectionId,
}) => {
  const dispatch = useAppDispatch();
  const section = useAppSelector(selectDiningSectionById(sectionId));
  const selectedTableId = useAppSelector(selectSelectedTableId);
  const canCreateOrders = useAppSelector(selectCanCreateOrders);
  const effectiveOpenSectionId = useAppSelector(
    selectEffectiveOpenDiningSectionId,
  );

  if (!section) return null;

  const isOpen = effectiveOpenSectionId === section.id;

  const handleToggle = () => {
    if (isOpen) {
      dispatch(setOpenDiningSection(0));
    } else {
      dispatch(setOpenDiningSection(section.id));
    }
  };

  return (
    <div
      className={`border-b border-gray-200 transition-colors duration-300 ${
        section.hasRunningOrders ? 'app-bg' : 'bg-white'
      }`}
    >
      <button
        type="button"
        onClick={handleToggle}
        className="flex h-10 w-full items-center justify-between bg-transparent px-3 text-sm font-semibold uppercase tracking-wide text-black transition-colors duration-200 hover:bg-black/5 focus:outline-none"
      >
        <div className="flex flex-1 items-center gap-2">
          {section.runningOrderTableCount > 0 && (
            <span className="flex h-4 min-w-[16px] items-center justify-center text-xs font-semibold text-primary">
              {section.runningOrderTableCount}
            </span>
          )}
          <span className="truncate">{section.name}</span>
        </div>
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>

      <AnimatedCollapsible isOpen={isOpen}>
        <div className="max-h-[260px] space-y-2 overflow-y-auto bg-white p-2">
          {section.tables.length === 0 ? (
            <p className="p-2 text-center text-xs italic text-gray-500">
              No tables
            </p>
          ) : (
            [...section.tables]
              .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                  numeric: true,
                  sensitivity: 'base',
                }),
              )
              .map((table, index) => (
                <TableCard
                  key={table.id}
                  tableName={table.name}
                  billNumber={
                    table.has_running_order
                      ? table.bill_number || undefined
                      : undefined
                  }
                  isBillPrinted={
                    table.has_running_order
                      ? Boolean(table.is_bill_printed)
                      : false
                  }
                  isTourPrimary={isTourPrimary && index === 0}
                  isSelected={selectedTableId === table.id}
                  disabled={!table.has_running_order && !canCreateOrders}
                  title={
                    !table.has_running_order && !canCreateOrders
                      ? 'You do not have permission to create new orders'
                      : undefined
                  }
                  onClick={() => {
                    dispatch(
                      selectDiningTableOrder({
                        id: table.id,
                        name: table.name,
                        sectionId: section.id,
                        sectionName: section.name,
                      }),
                    );
                  }}
                />
              ))
          )}
        </div>
      </AnimatedCollapsible>
    </div>
  );
};
export default SectionCollapsible;
